import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import type { MaintenanceTask } from '@/types/maintenanceTask'
import type { UndoEndPayload } from '@/api/equipmentApi'
import { useAuth } from '@/providers/AuthProvider'
import { mintSigningToken } from '@/lib/signingToken'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SignatureConfirmPanel } from '@/components/renderer/process/SignatureConfirmPanel'

/**
 * Reopen a completed event (Undo End). Time-boxed server-side to a controlled
 * window; a reason is mandatory and audited (21 CFR Part 11).
 */
export function UndoEndModal({
  task,
  onClose,
  onConfirm,
}: {
  task: MaintenanceTask | null
  onClose: () => void
  onConfirm: (id: string, payload: UndoEndPayload) => void
}) {
  const { email, user } = useAuth()
  const signer = email ?? ''
  const signerName = user?.fullName || email || '' // display name (never the id/username)
  const [reason, setReason] = useState('')
  // Reopening a completed event is a signed act like any other transition — it
  // used to go through on a name alone.
  const [password, setPassword] = useState('')
  const [signError, setSignError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!task) return
    setReason('')
    setPassword('')
    setSignError(null)
    setBusy(false)
  }, [task])

  const canUndo = reason.trim() !== '' && signer !== '' && password !== '' && !busy

  const handleConfirm = async () => {
    if (!task || !canUndo) return
    setSignError(null)
    setBusy(true)
    try {
      const signingToken = await mintSigningToken(password)
      onConfirm(task.id, { signingToken, reason: reason.trim() })
    } catch (err) {
      setSignError(apiErrorMessage(err, 'Password could not be verified.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='gap-0 p-0 sm:max-w-md'>
        {task && (
          <>
            <div className='flex items-start gap-3 border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600'>
                <RotateCcw className='h-5 w-5' />
              </div>
              <div>
                <h2 className='text-lg font-semibold tracking-tight text-gray-900'>Undo End</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  Reopen {task.equipmentId} — Completed → Started
                </p>
              </div>
            </div>

            <div className='space-y-4 px-6 py-5'>
              <div className='space-y-1'>
                <p className='text-xs font-medium text-gray-500'>Reason (required, audited)</p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder='Why is this completed event being reopened?'
                  className='w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30'
                />
              </div>
              {/* The same signature panel every other transition uses. */}
              <SignatureConfirmPanel
                meaning='Reopened by'
                signerName={signerName}
                value={password}
                onChange={setPassword}
                onSubmit={() => void handleConfirm()}
              />
              {signError && <p className='text-xs font-medium text-red-600'>{signError}</p>}
            </div>

            <div className='flex items-center gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                disabled={!canUndo}
                onClick={() => void handleConfirm()}
                className='ml-auto inline-flex items-center gap-2 rounded-md bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-300'
              >
                <RotateCcw className='h-4 w-4' />
                Reopen
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
